import api from "@/lib/api";

// When a download uses responseType:"blob" and the server returns a JSON error,
// axios delivers the error body as a Blob — so `e.response.data.detail` is lost.
// This normalizes it back to a plain object so callers can show the real reason.
async function normalizeBlobError(e) {
  try {
    const d = e?.response?.data;
    if (d instanceof Blob) {
      const text = await d.text();
      try { e.response.data = JSON.parse(text); }
      catch { e.response.data = { detail: text || "Download failed" }; }
    }
  } catch { /* ignore */ }
  return e;
}

async function blobDownload(url, params, filename) {
  let r;
  try { r = await api.get(url, { params, responseType: "blob" }); } catch (e) { throw await normalizeBlobError(e); }
  const href = window.URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(href), 10000);
}

export const dms = {
  ownerActivityLog: (params) => api.get("/dms/owner/activity-log", { params }).then(r => r.data),
  // notifications
  notifications: () => api.get("/dms/notifications").then(r => r.data),
  markRead: (id) => api.post(`/dms/notifications/${id}/read`).then(r => r.data),
  markAllRead: () => api.post(`/dms/notifications/read-all`).then(r => r.data),

  // categories
  listCategories: () => api.get("/dms/categories").then(r => r.data),
  createCategory: (body) => api.post("/dms/categories", body).then(r => r.data),
  updateCategory: (id, body) => api.put(`/dms/categories/${id}`, body).then(r => r.data),
  deleteCategory: (id) => api.delete(`/dms/categories/${id}`).then(r => r.data),

  // products
  listProducts: () => api.get("/dms/products").then(r => r.data),
  createProduct: (body) => api.post("/dms/products", body).then(r => r.data),
  updateProduct: (id, body) => api.put(`/dms/products/${id}`, body).then(r => r.data),
  bulkMarginPreview: (body) => api.post(`/dms/products-bulk/margin/preview`, body).then(r => r.data),
  bulkMarginApply: (body) => api.post(`/dms/products-bulk/margin/apply`, body).then(r => r.data),
  bulkMarginUndo: (id) => api.post(`/dms/products-bulk/margin/undo/${id}`).then(r => r.data),
  bulkEditLog: () => api.get(`/dms/products-bulk/log`).then(r => r.data),
  deleteAllProducts: () => api.delete("/dms/products/all").then(r => r.data),
  priceHistory: (id) => api.get(`/dms/products/${id}/price-history`).then(r => r.data),

  // owner inventory
  ownerInventory: () => api.get("/dms/owner/inventory").then(r => r.data),
  ownerInvAdjust: (body) => api.post("/dms/owner/inventory/adjust", body).then(r => r.data),
  ownerDistributorInventory: (distributorId) => api.get("/dms/owner/distributor-inventory", { params: { distributor_id: distributorId } }).then(r => r.data),
  ownerDistributorInvAdjust: (body) => api.post("/dms/owner/distributor-inventory/adjust", body).then(r => r.data),
  stockLedger: (params = {}) => api.get("/dms/stock-ledger", { params }).then(r => r.data),

  // distributors
  listDistributors: () => api.get("/dms/distributors").then(r => r.data),
  createDistributor: (body) => api.post("/dms/distributors", body).then(r => r.data),
  getDistributor: (id) => api.get(`/dms/distributors/${id}`).then(r => r.data),
  updateDistributor: (id, body) => api.put(`/dms/distributors/${id}`, body).then(r => r.data),
  deleteDistributor: (id) => api.delete(`/dms/distributors/${id}`).then(r => r.data),
  getDistVisibility: (id) => api.get(`/dms/distributors/${id}/visibility`).then(r => r.data),
  setDistVisibility: (id, body) => api.put(`/dms/distributors/${id}/visibility`, body).then(r => r.data),

  // retailer prices (owner/TL sets distributor's selling price to retailer)
  getRetailerPrices: (did) => api.get(`/dms/distributors/${did}/retailer-prices`).then(r => r.data),
  setRetailerPrice: (did, body) => api.put(`/dms/distributors/${did}/retailer-prices`, body).then(r => r.data),

  // distributor browse
  browseProducts: (distributor_id) => api.get("/dms/distributor/browse", { params: distributor_id ? { distributor_id } : {} }).then(r => r.data),

  // primary orders
  placeOrder: (body) => api.post("/dms/primary-orders", body).then(r => r.data),
  distributorPriceLists: (circular_id, distributor_id) => api.get("/dms/distributor/price-lists", { params: { ...(circular_id ? { circular_id } : {}), ...(distributor_id ? { distributor_id } : {}) } }).then(r => r.data),
  setDefaultPriceList: (circular_id, distributor_id) => api.put("/dms/distributor/price-lists/default", { circular_id, distributor_id }).then(r => r.data),
  ownerDistPriceLists: (did) => api.get(`/dms/distributors/${did}/price-lists`).then(r => r.data),
  ownerSetDistPriceLists: (did, body) => api.put(`/dms/distributors/${did}/price-lists`, body).then(r => r.data),
  deleteOrder: (id) => api.delete(`/dms/primary-orders/${id}`).then(r => r.data),
  updatePrimaryOrder: (id, body) => api.put(`/dms/primary-orders/${id}`, body).then(r => r.data),
  updatePrimaryOrderMeta: (id, body) => api.put(`/dms/primary-orders/${id}/meta`, body).then(r => r.data),
  listOrders: (status) => api.get("/dms/primary-orders", { params: status ? { status } : {} }).then(r => r.data),
  getOrder: (id) => api.get(`/dms/primary-orders/${id}`).then(r => r.data),
  fulfillLine: (oid, body) => api.post(`/dms/primary-orders/${oid}/fulfill-line`, body).then(r => r.data),
  markReady: (oid, body = {}) => api.post(`/dms/primary-orders/${oid}/ready`, body).then(r => r.data),
  addPrimaryItem: (oid, body) => api.post(`/dms/primary-orders/${oid}/add-item`, body).then(r => r.data),
  removePrimaryItem: (oid, body) => api.post(`/dms/primary-orders/${oid}/remove-item`, body).then(r => r.data),
  unreadyOrder: (oid) => api.post(`/dms/primary-orders/${oid}/unready`).then(r => r.data),
  markReceived: (oid) => api.post(`/dms/primary-orders/${oid}/receive`).then(r => r.data),
  updateEwayBill: (ebid, body) => api.put(`/dms/ebills/${ebid}/eway-bill`, body).then(r => r.data),

  // attachments
  listAttachments: (refId) => api.get(`/dms/attachments`, { params: { reference_id: refId } }).then(r => r.data),
  addAttachment: (body) => api.post(`/dms/attachments`, body).then(r => r.data),

  // primary ledger
  primaryLedger: (distributor_id) => api.get(`/dms/ledger/primary`, { params: distributor_id ? { distributor_id } : {} }).then(r => r.data),
  recordPrimaryPayment: (body) => api.post(`/dms/ledger/primary/payment`, body).then(r => r.data),
  submitPrimaryPayment: (body) => api.post(`/dms/ledger/primary/payment/submit`, body).then(r => r.data),
  confirmPrimaryPayment: (entryId) => api.post(`/dms/ledger/primary/payment/${entryId}/confirm`).then(r => r.data),
  collections: (params = {}) => api.get(`/dms/collections`, { params }).then(r => r.data),
  deleteCollection: (id, reason) => api.delete(`/dms/collections/${id}`, { params: { reason } }).then(r => r.data),

  // === Iteration 2 ===

  // retailers
  listRetailers: (params = {}) => api.get("/dms/retailers", { params }).then(r => r.data),
  createRetailer: (body) => api.post("/dms/retailers", body).then(r => r.data),
  distSalespersons: (did) => api.get(`/dms/distributors/${did}/salespersons`).then(r => r.data),
  getRetailer: (id) => api.get(`/dms/retailers/${id}`).then(r => r.data),
  updateRetailer: (id, body) => api.put(`/dms/retailers/${id}`, body).then(r => r.data),
  updateDistRetailerProfile: (id, body) => api.put(`/dms/distributor/retailers/${id}/profile`, body).then(r => r.data),
  deleteRetailer: (id) => api.delete(`/dms/retailers/${id}`).then(r => r.data),
  listDeletedRetailers: () => api.get("/dms/retailers-trash").then(r => r.data),
  restoreRetailer: (id) => api.post(`/dms/retailers/${id}/restore`).then(r => r.data),
  getRetVisibility: (id) => api.get(`/dms/retailers/${id}/visibility`).then(r => r.data),
  setRetVisibility: (id, body) => api.put(`/dms/retailers/${id}/visibility`, body).then(r => r.data),
  setRetVisibilityBulk: (body) => api.put("/dms/retailer-visibility/bulk", body).then(r => r.data),
  getRetVisibilityHidden: () => api.get("/dms/retailer-visibility/hidden").then(r => r.data),
  getRetMode: (id) => api.get(`/dms/retailers/${id}/selling-mode`).then(r => r.data),
  setRetMode: (id, body) => api.put(`/dms/retailers/${id}/selling-mode`, body).then(r => r.data),

  // retailer browse & order
  retailerBrowse: (retailer_id) => api.get("/dms/retailer/browse", { params: retailer_id ? { retailer_id } : {} }).then(r => r.data),
  placeSecondaryOrder: (body) => api.post("/dms/secondary-orders", body).then(r => r.data),
  listSecondaryOrders: (status) => api.get("/dms/secondary-orders", { params: status ? { status } : {} }).then(r => r.data),
  getSecondaryOrder: (id) => api.get(`/dms/secondary-orders/${id}`).then(r => r.data),
  dispatchSecondary: (oid, body) => api.post(`/dms/secondary-orders/${oid}/dispatch`, body).then(r => r.data),
  generateInvoiceSecondary: (oid, body = {}) => api.post(`/dms/secondary-orders/${oid}/invoice`, body).then(r => r.data),
  distributorStockLots: (product_id, distributor_id) => api.get("/dms/distributor/stock-lots", { params: { product_id, distributor_id } }).then(r => r.data),
  getOrderChallan: (oid) => api.get(`/dms/secondary-orders/${oid}/challan`).then(r => r.data),
  // Phase 1 additions:
  cancelSecondaryOrder: (oid, reason) => api.post(`/dms/secondary-orders/${oid}/cancel`, { reason }).then(r => r.data),
  softDeleteSecondaryOrder: (oid) => api.post(`/dms/secondary-orders/${oid}/soft-delete`).then(r => r.data),
  listDeletedSecondaryOrders: () => api.get("/dms/secondary-orders-trash").then(r => r.data),
  restoreSecondaryOrder: (oid) => api.post(`/dms/secondary-orders/${oid}/restore`).then(r => r.data),
  updateSecondaryOrder: (oid, body) => api.put(`/dms/secondary-orders/${oid}`, body).then(r => r.data),
  ownerEditAllowed: () => api.get("/dms/owner-edit-allowed").then(r => r.data),
  ownerEditOrder: (oid, body) => api.post(`/dms/secondary-orders/${oid}/owner-edit`, body).then(r => r.data),

  // secondary ledger
  // Point 70: accept either a retailer id string or a params object
  secondaryLedger: (arg = {}) => {
    const params = (typeof arg === "string") ? { retailer_id: arg } : (arg || {});
    return api.get(`/dms/ledger/secondary`, { params }).then(r => r.data);
  },
  recordSecondaryPayment: (body) => api.post(`/dms/ledger/secondary/payment`, body).then(r => r.data),
  uploadPaymentProof: (entryId, file) => { const fd = new FormData(); fd.append("file", file); return api.post(`/dms/ledger/secondary/payment/${entryId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data); },
  paymentProofBlob: (fid) => api.get(`/dms/ledger/secondary/payment-proof/${fid}`, { responseType: "blob" }).then(r => r.data),
  secondaryPendingInvoices: (retailer_id) => api.get(`/dms/ledger/secondary/pending-invoices`, { params: { retailer_id } }).then(r => r.data),

  // assignments
  listTlDistributors: (params = {}) => api.get("/dms/assignments/tl-distributors", { params }).then(r => r.data),
  assignTlDist: (body) => api.post("/dms/assignments/tl-distributors", body).then(r => r.data),
  unassignTlDist: (tl, did) => api.delete("/dms/assignments/tl-distributors", { params: { team_leader_id: tl, distributor_id: did } }).then(r => r.data),
  listSpDistributors: (params = {}) => api.get("/dms/assignments/sp-distributors", { params }).then(r => r.data),
  assignSpDist: (body) => api.post("/dms/assignments/sp-distributors", body).then(r => r.data),
  unassignSpDist: (sp, did) => api.delete("/dms/assignments/sp-distributors", { params: { salesperson_id: sp, distributor_id: did } }).then(r => r.data),
  listRmTls: (params = {}) => api.get("/dms/assignments/rm-tls", { params }).then(r => r.data),
  assignRmTl: (body) => api.post("/dms/assignments/rm-tls", body).then(r => r.data),
  unassignRmTl: (rm, tl) => api.delete("/dms/assignments/rm-tls", { params: { regional_manager_id: rm, team_leader_id: tl } }).then(r => r.data),
  listTlSalespersons: (params = {}) => api.get("/dms/assignments/tl-salespersons", { params }).then(r => r.data),
  assignTlSp: (body) => api.post("/dms/assignments/tl-salespersons", body).then(r => r.data),
  unassignTlSp: (tl, sp) => api.delete("/dms/assignments/tl-salespersons", { params: { team_leader_id: tl, salesperson_id: sp } }).then(r => r.data),

  // users
  listUsers: (role) => api.get("/dms/users", { params: role ? { role } : {} }).then(r => r.data),

  // punch
  punchIn: (body) => api.post("/dms/punch/in", body).then(r => r.data),
  punchOut: (body) => api.post("/dms/punch/out", body).then(r => r.data),
  punchToday: () => api.get("/dms/punch/today").then(r => r.data),
  dayPlanToday: () => api.get("/dms/dayplan/today").then(r => r.data),
  dayPlanCreate: (body) => api.post("/dms/dayplan/plan", body).then(r => r.data),
  dayPlanEdit: (body) => api.put("/dms/dayplan/plan", body).then(r => r.data),
  dayReportDraft: () => api.get("/dms/dayplan/report/draft").then(r => r.data),
  dayReportSubmit: (body) => api.post("/dms/dayplan/report/submit", body).then(r => r.data),
  dayPendingReport: () => api.get("/dms/dayplan/pending-report").then(r => r.data),
  dayReportComplete: (body) => api.post("/dms/dayplan/report/complete", body).then(r => r.data),
  dayReports: () => api.get("/dms/dayplan/reports").then(r => r.data),
  spTargets: () => api.get("/dms/dayplan/targets").then(r => r.data),
  spTargetSet: (id, body) => api.put(`/dms/dayplan/targets/${id}`, body).then(r => r.data),
  planVsActual: (params) => api.get("/dms/dayplan/plan-vs-actual", { params }).then(r => r.data),
  retailerSaveLocation: (id, body) => api.post(`/dms/retailers/${id}/save-location`, body).then(r => r.data),
  retailerResetLocation: (id) => api.post(`/dms/retailers/${id}/reset-location`, {}).then(r => r.data),
  punchAutoOut: (body) => api.post("/dms/punch/auto-out", body).then(r => r.data),

  // attendance (role-aware) + owner reopen + retailer login toggle
  attendance: () => api.get("/dms/attendance").then(r => r.data),
  hrAttendanceTree: (date) => api.get("/dms/hr/attendance-tree", { params: date ? { date } : {} }).then(r => r.data),
  hrAttendancePerformance: (days, late_after) => api.get("/dms/hr/attendance-performance", { params: { days, late_after } }).then(r => r.data),
  hrAttendanceReport: (month, late_after) => api.get("/dms/hr/attendance-report", { params: { month, late_after } }).then(r => r.data),
  hrPlanVsActual: (params) => api.get("/dms/hr/plan-vs-actual", { params }).then(r => r.data),
  hrPlanVsActualDetail: (user_id, date) => api.get("/dms/hr/plan-vs-actual/detail", { params: { user_id, date } }).then(r => r.data),
  hrPlanVsActualExport: async (params) => {
    try {
      const r = await api.get("/dms/hr/plan-vs-actual/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plan_vs_actual_${params.date_from || ""}_to_${params.date_to || ""}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      throw await normalizeBlobError(e);
    }
  },
  hrLocationTrail: (user_id, date) => api.get("/dms/hr/location-trail", { params: { user_id, date } }).then(r => r.data),
  hrPayrollReport: (month) => api.get("/dms/hr/payroll-report", { params: { month } }).then(r => r.data),
  // HR Phase B
  hrStaff: () => api.get("/dms/hr/staff").then(r => r.data),
  hrHolidays: (year) => api.get("/dms/hr/holidays", { params: { year } }).then(r => r.data),
  hrAddHoliday: (body) => api.post("/dms/hr/holidays", body).then(r => r.data),
  hrDeleteHoliday: (id) => api.delete(`/dms/hr/holidays/${id}`).then(r => r.data),
  hrLeavePolicy: () => api.get("/dms/hr/leave-policy").then(r => r.data),
  hrSaveLeavePolicy: (body) => api.put("/dms/hr/leave-policy", body).then(r => r.data),
  hrRequests: (status = "pending") => api.get("/dms/hr/requests", { params: { status } }).then(r => r.data),
  hrDecide: (kind, id, action, note) => api.post(`/dms/hr/requests/${kind}/${id}/decide`, { action, note }).then(r => r.data),
  hrAdvances: () => api.get("/dms/hr/advances").then(r => r.data),
  hrAddAdvance: (body) => api.post("/dms/hr/advances", body).then(r => r.data),
  hrCloseAdvance: (id, note) => api.post(`/dms/hr/advances/${id}/close`, { note }).then(r => r.data),
  hrAdjustments: (month) => api.get("/dms/hr/adjustments", { params: { month } }).then(r => r.data),
  hrAddAdjustment: (body) => api.post("/dms/hr/adjustments", body).then(r => r.data),
  hrDeleteAdjustment: (id) => api.delete(`/dms/hr/adjustments/${id}`).then(r => r.data),
  hrIncentiveRules: () => api.get("/dms/hr/incentive-rules").then(r => r.data),
  hrSaveIncentiveRules: (body) => api.put("/dms/hr/incentive-rules", body).then(r => r.data),
  hrSaveScoreWeights: (body) => api.put("/dms/hr/score-weights", body).then(r => r.data),
  hrScorecard: (params) => api.get("/dms/hr/scorecard", { params }).then(r => r.data),
  // HR Phase C
  hrScoreProfiles: () => api.get("/dms/hr/score-profiles").then(r => r.data),
  hrSaveScoreProfile: (role, body) => api.put(`/dms/hr/score-profiles/${role}`, body).then(r => r.data),
  hrSaveScoreOverride: (uid, body) => api.put(`/dms/hr/score-overrides/${uid}`, body).then(r => r.data),
  hrLetters: () => api.get("/dms/hr/letters").then(r => r.data),
  hrSaveLetter: (body) => api.post("/dms/hr/letters", body).then(r => r.data),
  hrDeleteLetter: (id) => api.delete(`/dms/hr/letters/${id}`).then(r => r.data),
  hrLetterPdf: (id, filename) => blobDownload(`/dms/hr/letters/${id}/pdf`, {}, filename),
  hrNotices: () => api.get("/dms/hr/notices").then(r => r.data),
  hrCreateNotice: (body) => api.post("/dms/hr/notices", body).then(r => r.data),
  hrWithdrawNotice: (id) => api.post(`/dms/hr/notices/${id}/withdraw`).then(r => r.data),
  myNotices: () => api.get("/dms/notices/active").then(r => r.data),
  dismissNotice: (id) => api.post(`/dms/notices/${id}/dismiss`).then(r => r.data),
  hrPolicyText: () => api.get("/dms/hr/policy-text").then(r => r.data),
  hrSavePolicyText: (text) => api.put("/dms/hr/policy-text", { text }).then(r => r.data),
  policyPdf: (year) => blobDownload("/dms/hr/policy.pdf", { year }, `Holiday_Leave_Policy_${year}.pdf`),
  hrPayslip: (month, user_id) => api.get("/dms/hr/payslip", { params: { month, user_id } }).then(r => r.data),
  selfHrSummary: () => api.get("/dms/hr-self/summary").then(r => r.data),
  selfApplyLeave: (body) => api.post("/dms/hr-self/leave", body).then(r => r.data),
  selfCancelLeave: (id) => api.post(`/dms/hr-self/leave/${id}/cancel`).then(r => r.data),
  selfRegularize: (body) => api.post("/dms/hr-self/regularize", body).then(r => r.data),
  selfPayslip: (month) => api.get("/dms/hr-self/payslip", { params: { month } }).then(r => r.data),
  hrSaveSalary: (body) => api.put("/dms/hr/salary-structure", body).then(r => r.data),
  hrSaveSalaryRules: (body) => api.put("/dms/hr/salary-rules", body).then(r => r.data),
  hrSalaryMaster: (month) => api.get("/dms/hr/salary-master", { params: { month } }).then(r => r.data),
  hrSalaryHistory: (user_id) => api.get("/dms/hr/salary-history", { params: { user_id } }).then(r => r.data),
  hrSalaryBulk: (body) => api.post("/dms/hr/salary-bulk", body).then(r => r.data),
  hrPayrollFinalize: (body) => api.post("/dms/hr/payroll-finalize", body).then(r => r.data),
  hrPayrollReopen: (body) => api.post("/dms/hr/payroll-reopen", body).then(r => r.data),
  reopenPunch: (spId) => api.post(`/dms/owner/punch/reopen/${spId}`).then(r => r.data),
  setRetailerLoginAccess: (rid, enabled) => api.put(`/dms/retailers/${rid}/login-access`, { enabled }).then(r => r.data),
  expenseAction: (id, action, note = "") => api.post(`/dms/expenses/${id}/action`, { action, note }).then(r => r.data),
  rmRetailers: () => api.get("/dms/rm/retailers").then(r => r.data),

  // dashboards
  ownerDashboard: () => api.get("/dms/dashboard/owner").then(r => r.data),
  ownerDashboardV2: (params) => api.get("/dms/dashboard/owner-v2", { params }).then(r => r.data),
  ownerInsightsV2: (params) => api.get("/dms/dashboard/owner-v2/insights", { params }).then(r => r.data),
  ownerMoreV2: (params) => api.get("/dms/dashboard/owner-v2/more", { params }).then(r => r.data),
  distributorDashboard: () => api.get("/dms/dashboard/distributor").then(r => r.data),
  distSaleLiters: (params = {}) => api.get("/dms/distributor/sale-liters", { params }).then(r => r.data),
  tlSaleLiters: (params = {}) => api.get("/dms/team-leader/sale-liters", { params }).then(r => r.data),
  rmSaleLiters: (params = {}) => api.get("/dms/rm/sale-liters", { params }).then(r => r.data),
  spSaleLiters: (params = {}) => api.get("/dms/salesperson/sale-liters", { params }).then(r => r.data),
  spMySalesSummary: (params = {}) => api.get("/dms/salesperson/my-sales-summary", { params }).then(r => r.data),
  distSalespersonsSelf: () => api.get("/dms/distributor/salespersons").then(r => r.data),
  distributorSalespersons: () => api.get("/dms/distributor/salespersons").then(r => r.data),
  distributorStockAlerts: () => api.get("/dms/distributor/stock-alerts").then(r => r.data),
  distributorMyStock: () => api.get("/dms/distributor/my-stock").then(r => r.data),
  tlDistributorStock: (did) => api.get(`/dms/team-leader/distributor-stock/${did}`).then(r => r.data),
  salespersonDashboard: () => api.get("/dms/dashboard/salesperson").then(r => r.data),
  salespersonCollections: (params = {}) => api.get("/dms/salesperson/collections", { params }).then(r => r.data),
  teamLeaderDashboard: () => api.get("/dms/dashboard/team-leader").then(r => r.data),
  regionalManagerDashboard: () => api.get("/dms/dashboard/regional-manager").then(r => r.data),
  retailerDashboard: () => api.get("/dms/dashboard/retailer").then(r => r.data),
  superAdminDashboard: () => api.get("/dms/dashboard/super-admin").then(r => r.data),

  // super admin
  adminUsers: () => api.get("/dms/admin/users").then(r => r.data),
  impersonate: (uid) => api.post(`/dms/admin/impersonate/${uid}`).then(r => r.data),

  // owner — complete user management (Phase 1)
  ownerListUsers: (role) => api.get("/dms/owner/users", { params: role ? { role } : {} }).then(r => r.data),
  ownerCreateUser: (body) => api.post("/dms/owner/users", body).then(r => r.data),
  ownerUpdateUser: (uid, body) => api.patch(`/dms/owner/users/${uid}`, body).then(r => r.data),
  ownerResetPassword: (uid, new_password) => api.post(`/dms/owner/users/${uid}/reset-password`, { new_password }).then(r => r.data),
  ownerRevealPassword: (uid, owner_password) => api.post(`/dms/owner/users/${uid}/reveal-password`, { owner_password }).then(r => r.data),
  ownerDeleteUser: (uid) => api.delete(`/dms/owner/users/${uid}`).then(r => r.data),
  ownerHierarchy: () => api.get("/dms/owner/hierarchy").then(r => r.data),
  hierChildren: (type, id) => api.get("/dms/owner/hierarchy/children", { params: id ? { type, id } : { type } }).then(r => r.data),
  hierSearch: (q) => api.get("/dms/owner/hierarchy/search", { params: { q } }).then(r => r.data),
  hierParents: (type) => api.get("/dms/owner/hierarchy/parents", { params: { type } }).then(r => r.data),
  bulkAssignRetailers: (retailer_ids, distributor_id) => api.post("/dms/owner/retailers/bulk-assign-distributor", { retailer_ids, distributor_id }).then(r => r.data),
  ownerDistTeamLeaders: (did) => api.get(`/dms/owner/distributors/${did}/team-leaders`).then(r => r.data),
  ownerTlSalespersons: (tlid, did) => api.get(`/dms/owner/team-leaders/${tlid}/salespersons`, { params: { distributor_id: did } }).then(r => r.data),
  ownerRetailerAssignInfo: (rid) => api.get(`/dms/owner/retailers/${rid}/assignment-info`).then(r => r.data),
  ownerAssignSalesperson: (rid, team_leader_id, salesperson_id) => api.put(`/dms/owner/retailers/${rid}/assign-salesperson`, { team_leader_id, salesperson_id }).then(r => r.data),
  ownerImpersonate: (uid) => api.post(`/dms/owner/impersonate/${uid}`).then(r => r.data),

  // live tracking (Phase 2 + 3)
  trackingPing: (body) => api.post("/dms/tracking/ping", body).then(r => r.data),
  trackingLive: () => api.get("/dms/tracking/live").then(r => r.data),
  trackingLiveUpdates: (params) => api.get("/dms/tracking/live-updates", { params }).then(r => r.data),
  trackingSalesperson: (sid, date) => api.get(`/dms/tracking/salesperson/${sid}`, { params: date ? { date } : {} }).then(r => r.data),
  trackingHistory: (sid, days = 30) => api.get(`/dms/tracking/salesperson/${sid}/history`, { params: { days } }).then(r => r.data),
  trackingRoutes: (sid, days = 7) => api.get(`/dms/tracking/salesperson/${sid}/routes`, { params: { days } }).then(r => r.data),
  // AI copilot report export (PDF / Excel) — returns a Blob
  aiExport: (body) => api.post("/ai/copilot/export", body, { responseType: "blob" }).then(r => r.data),
  // Owner: wipe demo/business data to a clean production state
  resetDemoData: () => api.post("/dms/owner/reset-demo-data").then(r => r.data),

  // Team Leader (Phase 4)
  tlDashboard: () => api.get("/dms/dashboard/team-leader").then(r => r.data),
  changePassword: (old_password, new_password) => api.post("/dms/auth/change-password", { old_password, new_password }).then(r => r.data),
  tlDistributors: () => api.get("/dms/tl/distributors").then(r => r.data),
  tlSalespersons: () => api.get("/dms/tl/salespersons").then(r => r.data),
  rmOrders: (params = {}) => api.get("/dms/rm/orders", { params }).then(r => r.data),
  rmOutstanding: () => api.get("/dms/rm/outstanding").then(r => r.data),
  rmFulfillment: () => api.get("/dms/rm/fulfillment").then(r => r.data),
  rmOutstandingRetailers: (distributor_id) => api.get("/dms/rm/outstanding/retailers", { params: { distributor_id } }).then(r => r.data),
  rmOutstandingDetail: (params = {}) => api.get("/dms/rm/outstanding-detail", { params }).then(r => r.data),
  tlStockAlerts: () => api.get("/dms/team-leader/stock-alerts").then(r => r.data),
  tlOutstanding: () => api.get("/dms/tl/outstanding").then(r => r.data),
  tlOutstandingRetailers: (distributor_id) => api.get("/dms/tl/outstanding/retailers", { params: { distributor_id } }).then(r => r.data),
  tlOutstandingDetail: (params = {}) => api.get("/dms/tl/outstanding-detail", { params }).then(r => r.data),
  spOutstanding: () => api.get("/dms/salesperson/outstanding").then(r => r.data),
  spOutstandingRetailers: (distributor_id) => api.get("/dms/salesperson/outstanding/retailers", { params: { distributor_id } }).then(r => r.data),
  spDistributorStock: (distributor_id) => api.get("/dms/salesperson/distributor-stock", { params: { distributor_id } }).then(r => r.data),
  rmDistributorInventory: (distributor_id) => api.get("/dms/rm/distributor-inventory", { params: { distributor_id } }).then(r => r.data),
  tlOrders: (params = {}) => api.get("/dms/tl/orders", { params }).then(r => r.data),
  tlRetailers: () => api.get("/dms/tl/retailers").then(r => r.data),
  tlAttendance: () => api.get("/dms/tl/attendance").then(r => r.data),
  tlPunchIn: (body) => api.post("/dms/tl/punch/in", body).then(r => r.data),
  tlPunchOut: (body) => api.post("/dms/tl/punch/out", body).then(r => r.data),

  // Owner enhancements (Phase 4)
  ownerTlPerformance: () => api.get("/dms/owner/tl-performance").then(r => r.data),
  ownerDistributorSales: (did) => api.get(`/dms/owner/distributor-sales/${did}`).then(r => r.data),

  // ════════════════════════════════════════════════════════════════════════
  // GO OIL — Enterprise Coupon & Reward Engine  (/dms/coupons/*)
  // ════════════════════════════════════════════════════════════════════════
  // Batches
  cpnCreateBatch: (body) => api.post("/dms/coupons/batches", body).then(r => r.data),
  cpnListBatches: (params = {}) => api.get("/dms/coupons/batches", { params }).then(r => r.data),
  cpnGetBatch: (bid) => api.get(`/dms/coupons/batches/${bid}`).then(r => r.data),
  cpnActivateBatch: (bid) => api.post(`/dms/coupons/batches/${bid}/activate`).then(r => r.data),
  cpnMarkPrinted: (bid) => api.post(`/dms/coupons/batches/${bid}/mark-printed`).then(r => r.data),
  cpnIssueToProd: (bid) => api.post(`/dms/coupons/batches/${bid}/issue-to-production`).then(r => r.data),
  cpnDeactivateBatch: (bid) => api.post(`/dms/coupons/batches/${bid}/deactivate`).then(r => r.data),
  // v2 — per-coupon and range activation
  cpnActivateCoupon: (cid) => api.post(`/dms/coupons/coupons/${cid}/activate`).then(r => r.data),
  cpnDeactivateCoupon: (cid) => api.post(`/dms/coupons/coupons/${cid}/deactivate`).then(r => r.data),
  cpnActivateRange: (body) => api.post(`/dms/coupons/activate-range`, body).then(r => r.data),
  cpnActivateRangePreview: (body) => api.post(`/dms/coupons/activate-range/preview`, body).then(r => r.data),
  cpnDeactivateRange: (body) => api.post(`/dms/coupons/deactivate-range`, body).then(r => r.data),
  cpnBulkActivate: (coupon_ids) => api.post(`/dms/coupons/coupons/bulk-activate`, { coupon_ids }).then(r => r.data),
  cpnBulkDeactivate: (coupon_ids) => api.post(`/dms/coupons/coupons/bulk-deactivate`, { coupon_ids }).then(r => r.data),
  cpnBulkVoid: (coupon_ids, reason) => api.post(`/dms/coupons/coupons/bulk-void`, { coupon_ids, reason }).then(r => r.data),
  cpnVoidSerial: (serial, reason) => api.post(`/dms/coupons/coupons/void-by-serial`, { serial, reason }).then(r => r.data),
  cpnDeleteBatch: (bid) => api.delete(`/dms/coupons/batches/${bid}`).then(r => r.data),
  cpnRestoreBatch: (bid) => api.post(`/dms/coupons/batches/${bid}/restore`).then(r => r.data),
  cpnDeleteBox: (bid) => api.delete(`/dms/coupons/boxes/${bid}`).then(r => r.data),
  cpnRestoreBox: (bid) => api.post(`/dms/coupons/boxes/${bid}/restore`).then(r => r.data),
  cpnListDeletedItems: () => api.get(`/dms/coupons/deleted-items`).then(r => r.data),
  cpnRestoreDeletedItem: (type, id) => api.post(`/dms/coupons/deleted-items/${type}/${id}/restore`).then(r => r.data),
  // v2 — Void / Cancel (Settings) — by serial or by batch, with audit-safe recovery
  cpnVoidBySerialPreview: (body) => api.post(`/dms/coupons/coupons/void-by-serial/preview`, body).then(r => r.data),
  cpnVoidBySerial: (body) => api.post(`/dms/coupons/coupons/void-by-serial`, body).then(r => r.data),
  cpnVoidBatchPreview: (body) => api.post(`/dms/coupons/coupons/void-batch/preview`, body).then(r => r.data),
  cpnVoidBatch: (body) => api.post(`/dms/coupons/coupons/void-batch`, body).then(r => r.data),
  cpnRecoverCoupon: (cid, body = {}) => api.post(`/dms/coupons/coupons/${cid}/recover`, body).then(r => r.data),
  cpnCouponQrPayload: (cid) => api.get(`/dms/coupons/coupons/${cid}/qr-payload`).then(r => r.data),
  cpnCouponQrImageBlob: async (cid, size = 6) => {
    const r = await api.get(`/dms/coupons/coupons/${cid}/qr-image`, {
      params: { size }, responseType: "blob",
    });
    return URL.createObjectURL(r.data);
  },
  cpnExportPdfUrl: (bid) => (process.env.REACT_APP_BACKEND_URL || "") + `/api/dms/coupons/batches/${bid}/export-pdf`,
  cpnExportXlsxUrl: (bid) => (process.env.REACT_APP_BACKEND_URL || "") + `/api/dms/coupons/batches/${bid}/export-xlsx`,
  cpnExportPdf: async (bid, filename, opts = {}) => {
    try {
      const params = {};
      if (opts.diameter_mm) params.diameter_mm = opts.diameter_mm;
      if (opts.side) params.side = opts.side;
      const r = await api.get(`/dms/coupons/batches/${bid}/export-pdf`, { responseType: "blob", params });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename || `batch_${bid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { throw await normalizeBlobError(e); }
  },
  cpnExportPdfBlob: async (bid, opts = {}) => {
    const params = {};
    if (opts.diameter_mm) params.diameter_mm = opts.diameter_mm;
    const r = await api.get(`/dms/coupons/batches/${bid}/export-pdf`, { responseType: "blob", params });
    return new Blob([r.data], { type: "application/pdf" });
  },
  // Async (background) PDF generation — reliable for large batches that would
  // otherwise exceed the proxy timeout. start → poll → download.
  cpnExportPdfJobStart: (bid, opts = {}) =>
    api.post(`/dms/coupons/batches/${bid}/export-pdf-async`, null, { params: opts.side ? { side: opts.side } : {} }).then(r => r.data),
  cpnPdfJobStatus: (jobId) => api.get(`/dms/coupons/pdf-jobs/${jobId}`).then(r => r.data),
  cpnExportPdfViaJob: async (bid, filename, opts = {}) => {
    try {
      const start = await api.post(`/dms/coupons/batches/${bid}/export-pdf-async`, null,
        { params: opts.side ? { side: opts.side } : {} }).then(r => r.data);
      const jobId = start.job_id;
      // poll until ready (up to ~4 min)
      for (let i = 0; i < 120; i++) {
        await new Promise(res => setTimeout(res, 2000));
        const st = await api.get(`/dms/coupons/pdf-jobs/${jobId}`).then(r => r.data);
        if (st.status === "done") break;
        if (st.status === "error") throw new Error(st.error || "PDF generation failed");
        if (i === 119) throw new Error("PDF generation timed out");
      }
      const r = await api.get(`/dms/coupons/pdf-jobs/${jobId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename || `batch_${bid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { throw await normalizeBlobError(e); }
  },
  cpnCreateShareLink: (bid, body = {}) =>
    api.post(`/dms/coupons/batches/${bid}/share-link`, body).then(r => r.data),
  // Mixed print (multiple batches / serial range) + Print History
  cpnPrintMixedPreview: (body) => api.post(`/dms/coupons/print-mixed/preview`, body).then(r => r.data),
  cpnPrintMixed: async (body, filename) => {
    try {
      const r = await api.post(`/dms/coupons/print-mixed`, body, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename || `coupons.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { throw await normalizeBlobError(e); }
  },
  cpnPrintHistory: () => api.get(`/dms/coupons/print-history`).then(r => r.data),
  cpnPrintHistoryDownload: async (hid, filename) => {
    try {
      const r = await api.get(`/dms/coupons/print-history/${hid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = filename || `reprint.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { throw await normalizeBlobError(e); }
  },
  cpnPrintHistoryDelete: (hid) => api.delete(`/dms/coupons/print-history/${hid}`).then(r => r.data),
  cpnExportXlsx: async (bid, filename) => {
    const r = await api.get(`/dms/coupons/batches/${bid}/export-xlsx`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a"); a.href = url; a.download = filename || `batch_${bid}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  },
  // Coupons
  cpnListCoupons: (params = {}) => api.get("/dms/coupons", { params }).then(r => r.data),
  cpnGetCoupon: (cid) => api.get(`/dms/coupons/detail/${cid}`).then(r => r.data),
  // Sales Officer (salesperson) scan
  cpnSoRetailers: () => api.get("/dms/coupons/so/retailers").then(r => r.data),
  cpnScan: (body) => api.post("/dms/coupons/scan", body).then(r => r.data),
  cpnScanPreview: (body) => api.post("/dms/coupons/scan/preview", body).then(r => r.data),
  // Box management
  cpnCreateBox: (body = {}) => api.post("/dms/coupons/boxes", body).then(r => r.data),
  cpnListBoxes: (params = {}) => api.get("/dms/coupons/boxes", { params }).then(r => r.data),
  cpnGetBox: (bid) => api.get(`/dms/coupons/boxes/${bid}`).then(r => r.data),
  cpnBoxAssignCoupons: (bid, body) => api.post(`/dms/coupons/boxes/${bid}/assign-coupons`, body).then(r => r.data),
  cpnBoxAssignDistributor: (bid, body) => api.post(`/dms/coupons/boxes/${bid}/assign-distributor`, body).then(r => r.data),
  cpnBoxStats: () => api.get("/dms/coupons/boxes/stats").then(r => r.data),
  cpnBoxScanHistory: (bid) => api.get(`/dms/coupons/boxes/${bid}/scan-history`).then(r => r.data),
  cpnBoxLabelPdf: async (bid) => {
    const r = await api.get(`/dms/coupons/boxes/${bid}/label-pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    window.open(url, "_blank");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  },
  // Retailer scan permission
  cpnGetScanPermission: () => api.get("/dms/coupons/scan-permission").then(r => r.data),
  cpnSetScanPermission: (enabled) => api.put("/dms/coupons/scan-permission", { enabled }).then(r => r.data),
  // Retailer self-scan
  cpnRetailerScanPreview: (body) => api.post("/dms/coupons/retailer/scan/preview", body).then(r => r.data),
  cpnRetailerScan: (body) => api.post("/dms/coupons/retailer/scan", body).then(r => r.data),
  // Retailer wallet
  cpnRetailerWallet: () => api.get("/dms/coupons/retailer/wallet").then(r => r.data),
  cpnRetailerTransactions: (wallet_type) => api.get("/dms/coupons/retailer/transactions", { params: wallet_type ? { wallet_type } : {} }).then(r => r.data),
  cpnRetailerCoupons: () => api.get("/dms/coupons/retailer/coupons").then(r => r.data),
  cpnRetailerRedemptions: () => api.get("/dms/coupons/retailer/redemptions").then(r => r.data),
  // Redemptions
  cpnCreateRedemption: (body) => api.post("/dms/coupons/redemptions", body).then(r => r.data),
  cpnListRedemptions: (params = {}) => api.get("/dms/coupons/redemptions", { params }).then(r => r.data),
  cpnApproveRedemption: (rid, body = {}) => api.post(`/dms/coupons/redemptions/${rid}/approve`, body).then(r => r.data),
  cpnRejectRedemption: (rid, reason) => api.post(`/dms/coupons/redemptions/${rid}/reject`, { reason }).then(r => r.data),
  // Distributor
  cpnDistSummary: () => api.get("/dms/coupons/dist/summary").then(r => r.data),
  cpnDistCreditNotes: () => api.get("/dms/coupons/dist/credit-notes").then(r => r.data),
  cpnDistDispatchAdvices: () => api.get("/dms/coupons/dist/dispatch-advices").then(r => r.data),
  // Distributor self-scan (credits distributor's own wallet)
  cpnDistScanPreview: (body) => api.post("/dms/coupons/distributor/scan/preview", body).then(r => r.data),
  cpnDistScan: (body) => api.post("/dms/coupons/distributor/scan", body).then(r => r.data),
  cpnDistWallet: () => api.get("/dms/coupons/distributor/wallet").then(r => r.data),
  cpnDistTransactions: () => api.get("/dms/coupons/distributor/transactions").then(r => r.data),
  // Owner — scan audit (who scanned, designation)
  cpnScanAudit: (params = {}) => api.get("/dms/coupons/audit", { params }).then(r => r.data),
  // Owner — CN / DA / Audit / Reports
  cpnCreditNotes: () => api.get("/dms/coupons/credit-notes").then(r => r.data),
  cpnDispatchAdvices: () => api.get("/dms/coupons/dispatch-advices").then(r => r.data),
  cpnMarkDispatched: (id) => api.post(`/dms/coupons/dispatch-advices/${id}/mark-dispatched`).then(r => r.data),
  cpnAuditLog: (params = {}) => api.get("/dms/coupons/audit-log", { params }).then(r => r.data),
  cpnReportsSummary: () => api.get("/dms/coupons/reports/summary").then(r => r.data),
  cpnReportsSalesperson: () => api.get("/dms/coupons/reports/salesperson").then(r => r.data),
  cpnReportsFraud: () => api.get("/dms/coupons/reports/fraud").then(r => r.data),
  cpnReportsDuplicate: () => api.get("/dms/coupons/reports/duplicate-scans").then(r => r.data),
  cpnReportsWalletSummary: () => api.get("/dms/coupons/reports/wallet-summary").then(r => r.data),
  // v2 — new reports & fraud dashboard
  cpnFraudDashboard: () => api.get("/dms/coupons/reports/fraud-dashboard").then(r => r.data),
  cpnReportsFraudFiltered: (params = {}) => api.get("/dms/coupons/reports/fraud", { params }).then(r => r.data),
  cpnReportsGeneration: () => api.get("/dms/coupons/reports/generation").then(r => r.data),
  cpnReportsActivation: (params = {}) => api.get("/dms/coupons/reports/activation", { params }).then(r => r.data),
  cpnReportsUnused: (params = {}) => api.get("/dms/coupons/reports/unused", { params }).then(r => r.data),
  cpnReportsInactive: (params = {}) => api.get("/dms/coupons/reports/inactive", { params }).then(r => r.data),
  cpnReportsUsage: (params = {}) => api.get("/dms/coupons/reports/usage", { params }).then(r => r.data),
  cpnReportsCashWallets: () => api.get("/dms/coupons/reports/cash-wallets").then(r => r.data),
  cpnReportsRewardWallets: () => api.get("/dms/coupons/reports/reward-wallets").then(r => r.data),
  cpnReportsRewardWalletsDetail: () => api.get("/dms/coupons/reports/reward-wallets/detail").then(r => r.data),
  cpnRewardWalletHistory: (retailerId) => api.get(`/dms/coupons/reports/reward-wallets/${retailerId}/history`).then(r => r.data),
  cpnReportsDistributorOutstanding: () => api.get("/dms/coupons/reports/distributor-outstanding").then(r => r.data),

  // Products import/export
  exportProducts: async () => {
    const r = await api.get("/dms/owner/products/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a"); a.href = url;
    a.download = `products_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  },
  importProducts: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/owner/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },

  // Regional Manager (Phase 5)
  rmDashboard: () => api.get("/dms/dashboard/regional-manager").then(r => r.data),
  rmTeamLeaders: () => api.get("/dms/rm/team-leaders").then(r => r.data),
  rmDistributors: () => api.get("/dms/rm/distributors").then(r => r.data),
  rmSalespersons: () => api.get("/dms/rm/salespersons").then(r => r.data),
  rmRegionPerformance: () => api.get("/dms/rm/region-performance").then(r => r.data),

  // print
  printEbill: (id) => api.get(`/dms/print/ebill/${id}`).then(r => r.data),
  printRetailerBill: (id) => api.get(`/dms/print/retailer-bill/${id}`).then(r => r.data),
  printChallan: (id) => api.get(`/dms/print/challan/${id}`).then(r => r.data),

  // settings (global — GST %)
  getSettings: () => api.get("/dms/settings").then(r => r.data),
  updateSettings: (body) => api.put("/dms/settings", body).then(r => r.data),

  // My bank / payment details (distributor + retailer self-service)
  getMyBank: () => api.get("/dms/my/bank").then(r => r.data),
  updateMyBank: (bank) => api.put("/dms/my/bank", { bank }).then(r => r.data),
  getMyProfile: () => api.get("/dms/my/profile").then(r => r.data),
  updateMyProfile: (body) => api.put("/dms/my/profile", body).then(r => r.data),

  // Phase 2A: Financial Year close
  fyClose: (lock_date) => api.post("/dms/finance/fy-close", { lock_date }).then(r => r.data),

  // Phase 2A: Expenses
  listExpenses: (params = {}) => api.get("/dms/expenses", { params }).then(r => r.data),
  createExpense: (body) => api.post("/dms/expenses", body).then(r => r.data),
  updateExpense: (id, body) => api.put(`/dms/expenses/${id}`, body).then(r => r.data),
  deleteExpense: (id) => api.delete(`/dms/expenses/${id}`).then(r => r.data),
  expenseCategories: () => api.get("/dms/expenses/categories").then(r => r.data),

  // Phase 2A: Editable invoice/bill numbers
  updateEbillNumber: (id, ebill_no) => api.put(`/dms/ebills/${id}/number`, { ebill_no }).then(r => r.data),
  updateRetailerBillNumber: (id, bill_no) => api.put(`/dms/retailer-bills/${id}/number`, { bill_no }).then(r => r.data),
  updateChallanNumber: (oid, challan_no) => api.put(`/dms/secondary-orders/${oid}/challan-number`, { challan_no }).then(r => r.data),
  updateRetailerBillEwayBill: (id, body) => api.put(`/dms/retailer-bills/${id}/eway-bill`, body).then(r => r.data),
  getNumberSettings: (distributorId) => api.get(`/dms/number-settings`, { params: distributorId ? { distributor_id: distributorId } : {} }).then(r => r.data),
  updateNumberSettings: (docType, body) => api.put(`/dms/number-settings/${docType}`, body).then(r => r.data),
  voidEbill: (id) => api.post(`/dms/ebills/${id}/void`).then(r => r.data),
  deleteRetailerBill: (id) => api.delete(`/dms/retailer-bills/${id}`).then(r => r.data),

  // Phase 2B: Cash & Bank
  listBankAccounts: () => api.get("/dms/bank-accounts").then(r => r.data),
  createBankAccount: (b) => api.post("/dms/bank-accounts", b).then(r => r.data),
  updateBankAccount: (id, b) => api.put(`/dms/bank-accounts/${id}`, b).then(r => r.data),
  deleteBankAccount: (id) => api.delete(`/dms/bank-accounts/${id}`).then(r => r.data),
  listBankTxns: (params = {}) => api.get("/dms/bank-transactions", { params }).then(r => r.data),
  createBankTxn: (b) => api.post("/dms/bank-transactions", b).then(r => r.data),
  deleteBankTxn: (id) => api.delete(`/dms/bank-transactions/${id}`).then(r => r.data),
  listCashRegister: (params = {}) => api.get("/dms/cash-register", { params }).then(r => r.data),
  createCashEntry: (b) => api.post("/dms/cash-register", b).then(r => r.data),
  deleteCashEntry: (id) => api.delete(`/dms/cash-register/${id}`).then(r => r.data),
  listCheques: (params = {}) => api.get("/dms/cheques", { params }).then(r => r.data),
  createCheque: (b) => api.post("/dms/cheques", b).then(r => r.data),
  updateCheque: (id, b) => api.put(`/dms/cheques/${id}`, b).then(r => r.data),
  deleteCheque: (id) => api.delete(`/dms/cheques/${id}`).then(r => r.data),
  listLoans: () => api.get("/dms/loan-accounts").then(r => r.data),
  createLoan: (b) => api.post("/dms/loan-accounts", b).then(r => r.data),
  updateLoan: (id, b) => api.put(`/dms/loan-accounts/${id}`, b).then(r => r.data),
  deleteLoan: (id) => api.delete(`/dms/loan-accounts/${id}`).then(r => r.data),
  listLoanTxns: (loan_id) => api.get("/dms/loan-transactions", { params: { loan_id } }).then(r => r.data),
  createLoanTxn: (b) => api.post("/dms/loan-transactions", b).then(r => r.data),

  // Phase 2B: Godowns + Stock Transfer
  listGodowns: () => api.get("/dms/godowns").then(r => r.data),
  createGodown: (b) => api.post("/dms/godowns", b).then(r => r.data),
  updateGodown: (id, b) => api.put(`/dms/godowns/${id}`, b).then(r => r.data),
  deleteGodown: (id) => api.delete(`/dms/godowns/${id}`).then(r => r.data),
  godownInventory: (id) => api.get(`/dms/godowns/${id}/inventory`).then(r => r.data),
  listStockTransfers: (params = {}) => api.get("/dms/stock-transfers", { params }).then(r => r.data),
  getStockTransfer: (id) => api.get(`/dms/stock-transfers/${id}`).then(r => r.data),
  createStockTransfer: (b) => api.post("/dms/stock-transfers", b).then(r => r.data),
  toggleStopSale: (enabled) => api.put("/dms/settings/stop-sale", { enabled }).then(r => r.data),

  // Phase 2C: Import/Export
  exportParties: () => api.get("/dms/parties/export", { responseType: "blob" }).then(r => r.data),
  importParties: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/parties/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  exportSaleBills: (params = {}) => api.get("/dms/sale-bills/export", { params, responseType: "blob" }).then(r => r.data),
  exportPayments: (params = {}) => api.get("/dms/payments/export", { params, responseType: "blob" }).then(r => r.data),
  exportProducts: () => api.get("/dms/owner/products/export", { responseType: "blob" }).then(r => r.data),
  importProducts: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/owner/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  importPriceCircular: (file, title, columnMap) => {
    const fd = new FormData(); fd.append("file", file);
    if (title) fd.append("title", title);
    if (columnMap) fd.append("column_map", columnMap);
    return api.post("/dms/owner/products/import-circular", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  importPreview: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/owner/products/import-circular/preview", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  importTemplate: async () => {
    const r = await api.get("/dms/owner/products/import-template", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a"); a.href = url;
    a.download = "GO_OIL_price_list_template.xlsx";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  },
  // Item 10: Sale Bill Import + Payment Import + templates
  importSaleBills: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/sale-bills/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  importPayments: (file) => {
    const fd = new FormData(); fd.append("file", file);
    return api.post("/dms/payments/import", fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  saleBillsTemplate: () => api.get("/dms/sale-bills/import-template", { responseType: "blob" }).then(r => r.data),
  paymentsTemplate: () => api.get("/dms/payments/import-template", { responseType: "blob" }).then(r => r.data),

  // Phase 2C: Direct Sales
  createDirectSale: (b) => api.post("/dms/direct-sales", b).then(r => r.data),
  listDirectSales: () => api.get("/dms/direct-sales").then(r => r.data),
  deleteDirectSale: (id) => api.delete(`/dms/direct-sales/${id}`).then(r => r.data),
  fullEditDirectSale: (id, b) => api.put(`/dms/direct-sales/${id}/full-edit`, b).then(r => r.data),
  fullEditDirectSale: (id, b) => api.put(`/dms/direct-sales/${id}/full-edit`, b).then(r => r.data),

  // Phase 2C: Documents (stubs)
  listDocuments: (params = {}) => api.get("/dms/documents", { params }).then(r => r.data),
  getDocument: (id) => api.get(`/dms/documents/${id}`).then(r => r.data),
  createDocument: (b) => api.post("/dms/documents", b).then(r => r.data),
  updateDocument: (id, b) => api.put(`/dms/documents/${id}`, b).then(r => r.data),
  deleteDocument: (id) => api.delete(`/dms/documents/${id}`).then(r => r.data),
  printDocument: (id) => api.get(`/dms/documents/${id}/print`).then(r => r.data),

  // Phase 2C: PO PDF
  printPurchaseOrder: (oid) => api.get(`/dms/print/purchase-order/${oid}`).then(r => r.data),

  // Phase 2C: Finance snapshot
  financeSnapshot: () => api.get("/dms/dashboard/finance-snapshot").then(r => r.data),

  // Phase 2C: Low-stock + reorder level
  setReorderLevel: (gid, body) => api.put(`/dms/godowns/${gid}/reorder-level`, body).then(r => r.data),
  listLowStock: () => api.get("/dms/godowns/low-stock").then(r => r.data),

  // price circulars
  listPriceCirculars: () => api.get("/dms/price-circulars").then(r => r.data),
  getPriceCircular: (cid) => api.get(`/dms/price-circulars/${cid}`).then(r => r.data),
  createPriceCircular: (body) => api.post("/dms/price-circulars", body).then(r => r.data),
  productCircularHistory: (pid) => api.get(`/dms/products/${pid}/circular-history`).then(r => r.data),

  // coupons — legacy scan endpoints REMOVED (see cpn* namespace below for the new
  // GO OIL enterprise coupon engine).

  // Phase 3: Reports
  reportsCatalog: () => api.get("/dms/reports/catalog").then(r => r.data),
  toggleReportFavorite: (rid) => api.post(`/dms/reports/favorites/toggle/${rid}`).then(r => r.data),
  runSaleReport: (params) => api.get("/dms/reports/sale/run", { params }).then(r => r.data),
  saleReportExportUrl: (params) => {
    const qs = new URLSearchParams(params || {}).toString();
    const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api/dms/reports/sale/export";
    return qs ? `${base}?${qs}` : base;
  },
  runReport: (reportId, params) => api.get(`/dms/reports/${reportId}/run`, { params }).then(r => r.data),
  reportExportUrl: (reportId, params) => {
    const qs = new URLSearchParams(params || {}).toString();
    const base = (process.env.REACT_APP_BACKEND_URL || "") + `/api/dms/reports/${reportId}/export`;
    return qs ? `${base}?${qs}` : base;
  },
  listSavedFilters: (reportId) => api.get(`/dms/reports/saved-filters/${reportId}`).then(r => r.data),
  saveFilter: (reportId, payload) => api.post(`/dms/reports/saved-filters/${reportId}`, payload).then(r => r.data),
  deleteSavedFilter: (id) => api.delete(`/dms/reports/saved-filters/${id}`).then(r => r.data),

  // Returns (primary: distributor->owner, secondary: retailer->distributor)
  createReturn: (b) => api.post("/dms/returns", b).then(r => r.data),
  listReturns: (params = {}) => api.get("/dms/returns", { params }).then(r => r.data),
  getReturn: (id) => api.get(`/dms/returns/${id}`).then(r => r.data),
  getReturnValue: (id) => api.get(`/dms/returns/${id}/value`).then(r => r.data),
  approveReturn: (id) => api.post(`/dms/returns/${id}/approve`).then(r => r.data),
  rejectReturn: (id, reason) => api.post(`/dms/returns/${id}/reject`, { reason }).then(r => r.data),
};

export function inr(n) {
  const v = Number(n || 0);
  // keep real paise/decimal precision — only show up to 2 decimals, and only when non-zero
  const hasDecimals = Math.abs(v - Math.round(v)) > 0.001;
  return "\u20B9 " + v.toLocaleString("en-IN", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

export function niceDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) +
           " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export function statusPill(status) {
  const map = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    partially_fulfilled: "bg-blue-100 text-blue-800 border-blue-200",
    fulfilled: "bg-emerald-100 text-emerald-800 border-emerald-200",
    ready_to_go: "bg-indigo-100 text-indigo-800 border-indigo-200",
    received: "bg-green-100 text-green-800 border-green-200",
    dispatched: "bg-indigo-100 text-indigo-800 border-indigo-200",
    completed: "bg-green-100 text-green-800 border-green-200",
    cancelled: "bg-rose-100 text-rose-800 border-rose-200",
    delivered: "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
  return map[status] || "bg-slate-100 text-slate-700 border-slate-200";
}
